/* =========================================================
	ANNUAL REPORT JS
========================================================= */

/* MARK: Imports & Dependencies
----------------------------------------------- */
import { Swiper } from 'swiper';
import { FreeMode } from 'swiper/modules';
import 'swiper/css';
import MicroModal from 'micromodal';
import Alpine from 'alpinejs';
import Chart from 'chart.js/auto';
import '../js/includes/accordion-cards';
import '../js/includes/people-cards';
import './includes/annual-report-chart-total-assets';
import '../js/includes/annual-report-chart-investment-types';
import AnnualReportCounter from '../js/includes/annual-report-counter';
import AOS from 'aos';
import 'aos/dist/aos.css'; // You can also use <link> for styles

AOS.init({
   easing: 'ease-in-out',
});

// Make Alpine and Chart available globally
window.Alpine = Alpine;
window.Chart = Chart; // Add this line

// Configure Swiper to use modules
Swiper.use([FreeMode]);

// Only register ScrollTrigger if it exists
// if (typeof ScrollTrigger !== 'undefined') {
//    gsap.registerPlugin(ScrollTrigger);
// }

/* MARK: Alpine.js Filter Controller
----------------------------------------------- */
document.addEventListener('alpine:init', () => {
   Alpine.data('filterController', () => ({
      activeView: 'state',
      activeFilter: 'all',
      mobileDropdownOpen: false,
      _filterStatus: '',

      init() {
         this.updateSlideVisibility();
         this.announceFilterChange();
      },

      toggleView() {
         this.activeView = this.activeView === 'state' ? 'sector' : 'state';
         this.activeFilter = 'all';
         this.updateSlideVisibility();
         this.announceFilterChange();
      },

      setFilter(filter) {
         // console.log('setFilter called with:', filter);
         this.activeFilter = filter;
         // console.log('About to call updateSlideVisibility...');
         this.updateSlideVisibility();
         // console.log('About to call announceFilterChange...');
         this.announceFilterChange();
         // console.log('About to call updateFilterButtonStates...');
         this.updateFilterButtonStates();
         // console.log('setFilter completed');
      },

      updateSlideVisibility() {
         // console.log('updateSlideVisibility called!');
         const slides = document.querySelectorAll('.slide-item');
         // console.log('Found slides:', slides.length);
         // console.log('Active filter:', this.activeFilter);

         slides.forEach((slide, index) => {
            let isActive = false;

            if (this.activeFilter === 'all') {
               isActive = true;
            } else if (this.activeFilter === 'bcNational') {
               // Special case: BC + NAT'L should match either 'BC' or 'National'
               const slideStateValue = slide.dataset.state;
               isActive =
                  slideStateValue === 'BC' || slideStateValue === 'National';
            } else {
               // Check both state and sector data attributes
               // This allows mobile dropdown to work with unified filter list
               const slideStateValue = slide.dataset.state;
               const slideSectorValue = slide.dataset.sector;

               // if (index < 3) { // Only log first 3 slides to avoid spam
               //    console.log(`Slide ${index}:`, {
               //       state: slideStateValue,
               //       sector: slideSectorValue,
               //       activeFilter: this.activeFilter
               //    });
               // }

               isActive =
                  slideStateValue === this.activeFilter ||
                  slideSectorValue === this.activeFilter;
            }

            // Toggle active class
            slide.classList.toggle('active', isActive);
            slide.setAttribute('aria-hidden', !isActive);

            // Update button accessibility
            const button = slide.querySelector('.slide-button');
            if (button) {
               button.disabled = !isActive;
               button.setAttribute('tabindex', isActive ? '0' : '-1');
            }
         });
      },

      updateFilterButtonStates() {
         // Update aria-pressed states for desktop filter buttons
         const filterButtons = document.querySelectorAll('.filter-btn');

         filterButtons.forEach((button) => {
            const isActive = button.dataset.filter === this.activeFilter;
            button.setAttribute('aria-pressed', isActive);
         });

         // Update active class for mobile dropdown buttons
         const mobileButtons = document.querySelectorAll(
            '.dropdown-item[data-filter]'
         );

         mobileButtons.forEach((button) => {
            const isActive = button.dataset.filter === this.activeFilter;
            button.classList.toggle('active', isActive);
         });
      },

      announceFilterChange() {
         const filterName =
            this.activeFilter === 'all'
               ? `All ${this.activeView === 'state' ? 'States' : 'Sectors'}`
               : this.getFilterDisplayName(this.activeFilter);

         this.filterStatus = `Showing projects for: ${filterName}`;
      },

      getFilterDisplayName(filter) {
         // Map filter codes to display names
         const names = {
            // State mappings (using full names as they appear in your HTML)
            Alaska: 'Alaska',
            Idaho: 'Idaho',
            Montana: 'Montana',
            Oregon: 'Oregon',
            Washington: 'Washington',
            bcNational: 'BC & National',

            // Sector mappings
            'arts-culture': 'Arts, Culture, & Education',
            'human-services': 'Civic Engagement & Community Services',
            health: 'Health & Environmental Stewardship',
            education: 'Nonprofit Leadership & Development',
            research: 'Scientific Research',
         };

         return names[filter] || filter;
      },

      get filterStatus() {
         return this._filterStatus;
      },

      set filterStatus(value) {
         this._filterStatus = value;
      },
   }));
});

/* MARK: Configuration
----------------------------------------------- */
// Swiper configuration constants
const SWIPER_CONFIG = {
   slidesPerView: 'auto',
   spaceBetween: 15,
   freeMode: {
      enabled: true,
      momentum: false,
   },
   loop: true,
   loopedSlides: 'auto',
   simulateTouch: true,
   allowTouchMove: true,
   watchOverflow: false,
   breakpoints: {
      640: {
         spaceBetween: 20,
      },
   },
};

// MicroModal configuration
const MICROMODAL_CONFIG = {
   disableScroll: true,
   openClass: 'is-open',
};

/* MARK: GSAP Entrance Animations (Intersection Observer)
----------------------------------------------- */
function initEntranceAnimations() {
   // Set initial states
   gsap.set('.slider-1 .swiper-wrapper', {
      x: -20,
      opacity: 0,
   });

   gsap.set('.slider-2 .swiper-wrapper', {
      x: 20,
      opacity: 0,
   });

   gsap.set('.slider-1', {
      marginLeft: -100,
   });

   gsap.set('.slider-2', {
      marginLeft: -40,
   });

   // Create Intersection Observer for entrance animations
   const entranceObserver = new IntersectionObserver(
      (entries) => {
         entries.forEach((entry) => {
            if (entry.isIntersecting) {
               // Trigger the entrance animation
               const entranceTimeline = gsap.timeline();

               entranceTimeline
                  .to(
                     '.slider-1 .swiper-wrapper',
                     {
                        x: 0,
                        opacity: 1,
                        duration: 1.5,
                        ease: 'power2.out',
                     },
                     0
                  )
                  .to(
                     '.slider-2 .swiper-wrapper',
                     {
                        x: 0,
                        opacity: 1,
                        duration: 1.5,
                        ease: 'power2.out',
                     },
                     0
                  );

               // Stop observing once triggered
               entranceObserver.unobserve(entry.target);
            }
         });
      },
      {
         threshold: 0.2, // Trigger when 20% visible
         rootMargin: '0px 0px -20% 0px', // Trigger slightly before fully in view
      }
   );

   // Start observing the slider container
   const container = document.querySelector('.dual-slider-container');
   if (container) {
      entranceObserver.observe(container);
   }
}

function initParallaxEffects() {
   // Check if we're not on mobile (matches your CSS media query)
   if (window.innerWidth < 768) {
      return;
   }

   // Respect reduced motion preference
   if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
   }

   let ticking = false;

   function updateScrollPosition() {
      const scrollY = window.pageYOffset;
      // Cap at 1000px scroll for max 20px movement (1000 * 0.02 = 20px)
      const movement = Math.min(scrollY, 1000);

      document.documentElement.style.setProperty('--scroll-y', `${movement}px`);
      ticking = false;
   }

   function requestTick() {
      if (!ticking) {
         requestAnimationFrame(updateScrollPosition);
         ticking = true;
      }
   }

   // Throttled scroll listener
   window.addEventListener('scroll', requestTick, { passive: true });

   // Initial call
   updateScrollPosition();
}

/* MARK: Swiper Configuration & Initialization
----------------------------------------------- */
function initSwipers() {
   // Find all swiper containers
   const swiperElements = document.querySelectorAll('.slider .swiper');

   // console.log('Found swipers:', swiperElements.length);

   // Initialize each one
   swiperElements.forEach((element, index) => {
      // console.log(`Initializing swiper ${index + 1}`);

      // Calculate different starting positions for staggered effect
      let initialSlide = 0;
      if (index === 0) {
         // First slider: start at slide 3 (roughly centered/offset)
         initialSlide = 3;
      } else if (index === 1) {
         // Second slider: start at slide 1 (slight offset)
         initialSlide = 1;
      }

      const swiper = new Swiper(element, {
         ...SWIPER_CONFIG,
         // Set different starting positions for brick wall effect
         initialSlide: initialSlide,
         on: {
            init: function () {
               // console.log(`Swiper ${index + 1} initialized`);
            },
         },
      });
   });
}

/* MARK: Instruction Hover Effects
----------------------------------------------- */
function initInstructionHovers() {
   const sliderElement = document.querySelector('.dual-slider');
   const topInstructions = document.querySelector(
      '.slider-instructions[data-instructions="top"]'
   );
   const bottomInstructions = document.querySelector(
      '.slider-instructions[data-instructions="bottom"]'
   );

   if (!sliderElement || !topInstructions || !bottomInstructions) {
      return;
   }

   // Set initial state - instructions are hidden
   topInstructions.style.opacity = '0';
   bottomInstructions.style.opacity = '0';

   // Show instructions on mouse enter
   sliderElement.addEventListener('mouseenter', () => {
      topInstructions.style.opacity = '1';
      bottomInstructions.style.opacity = '1';
   });

   // Hide instructions on mouse leave
   sliderElement.addEventListener('mouseleave', () => {
      topInstructions.style.opacity = '0';
      bottomInstructions.style.opacity = '0';
   });

   // Handle touch devices - show instructions on first touch, hide after delay
   let touchTimeout;

   sliderElement.addEventListener('touchstart', () => {
      // Clear any existing timeout
      if (touchTimeout) {
         clearTimeout(touchTimeout);
      }

      // Show instructions
      topInstructions.style.opacity = '1';
      bottomInstructions.style.opacity = '1';

      // Hide after 3 seconds of no interaction
      touchTimeout = setTimeout(() => {
         topInstructions.style.opacity = '0';
         bottomInstructions.style.opacity = '0';
      }, 3000);
   });

   // Reset timeout on touch move (user is actively interacting)
   sliderElement.addEventListener('touchmove', () => {
      if (touchTimeout) {
         clearTimeout(touchTimeout);
         touchTimeout = setTimeout(() => {
            topInstructions.style.opacity = '0';
            bottomInstructions.style.opacity = '0';
         }, 3000);
      }
   });
}

/* MARK: Modal System (MicroModal + Drag Prevention)
----------------------------------------------- */
function initModals() {
   // Initialize MicroModal with additional callbacks
   MicroModal.init({
      ...MICROMODAL_CONFIG,
      onShow: (modal) => {
         // Temporarily boost the containing section's z-index
         const grantHighlightsSection = document.getElementById(
            'ar-grant-highlights'
         );
         if (grantHighlightsSection) {
            grantHighlightsSection.style.zIndex = '110';
         }
      },
      onClose: (modal) => {
         // Remove the temporary z-index
         const grantHighlightsSection = document.getElementById(
            'ar-grant-highlights'
         );
         if (grantHighlightsSection) {
            grantHighlightsSection.style.removeProperty('z-index');
         }
      },
   });

   // Prevent modal opening during drag
   initSwiperModalPrevention();
}

function initSwiperModalPrevention() {
   // Wait for swipers to be initialized
   setTimeout(() => {
      const swiperElements = document.querySelectorAll('.slider .swiper');

      swiperElements.forEach((swiperEl) => {
         const swiperInstance = swiperEl.swiper;

         if (swiperInstance) {
            let swiperIsDragging = false;

            // Track Swiper's drag state
            swiperInstance.on('touchStart', () => {
               swiperIsDragging = false;
            });

            swiperInstance.on('touchMove', () => {
               swiperIsDragging = true;
            });

            swiperInstance.on('touchEnd', () => {
               // Small delay to let click event know about drag state
               setTimeout(() => {
                  swiperIsDragging = false;
               }, 50);
            });

            // Prevent modal on slides within this swiper
            const slides = swiperEl.querySelectorAll(
               '[data-micromodal-trigger]'
            );
            slides.forEach((slide) => {
               slide.addEventListener(
                  'click',
                  (e) => {
                     if (swiperIsDragging) {
                        e.preventDefault();
                        e.stopPropagation();
                        // console.log('Modal prevented - Swiper drag detected');
                     }
                  },
                  true
               );
            });
         }
      });
   }, 200);
}

/* MARK: Scroll-Triggered Navigation (Intersection Observer)
----------------------------------------------- */
function initScrollNavigation() {
   // Only enable on screens 768px and wider
   if (window.innerWidth < 768) {
      return;
   }

   const nav = document.querySelector('.ar-nav');
   const header = document.querySelector('.ar-header');

   if (!nav || !header) {
      console.warn('Navigation or header not found for scroll trigger');
      return;
   }

   // Set initial state - nav is hidden
   gsap.set(nav, {
      y: '-100%',
      opacity: 0,
   });

   let isNavVisible = false;

   // Create Intersection Observer for navigation
   const navObserver = new IntersectionObserver(
      (entries) => {
         entries.forEach((entry) => {
            // When header is NOT intersecting (scrolled past), show nav
            const shouldShowNav = !entry.isIntersecting;

            if (shouldShowNav && !isNavVisible) {
               showNavigation();
            } else if (!shouldShowNav && isNavVisible) {
               hideNavigation();
            }
         });
      },
      {
         threshold: 0,
         rootMargin: '0px 0px 0px 0px',
      }
   );

   // Start observing the header
   navObserver.observe(header);

   function showNavigation() {
      if (isNavVisible) return;

      gsap.to(nav, {
         y: '0%',
         opacity: 1,
         duration: 0.4,
         ease: 'power2.out',
      });

      isNavVisible = true;
   }

   function hideNavigation() {
      if (!isNavVisible) return;

      gsap.to(nav, {
         y: '-100%',
         opacity: 1,
         duration: 0.3,
         ease: 'power2.in',
      });

      isNavVisible = false;
   }
}

/* MARK: To-Top Link (Intersection Observer)
----------------------------------------------- */
function initToTopLink() {
   const header = document.querySelector('.ar-header');
   const toTopLink = document.querySelector('a.to-top');

   if (!header || !toTopLink) {
      return;
   }

   // Set initial state - hidden
   gsap.set(toTopLink, {
      opacity: 0,
      pointerEvents: 'none',
   });

   let isToTopVisible = false;

   // Create Intersection Observer for to-top link
   const toTopObserver = new IntersectionObserver(
      (entries) => {
         entries.forEach((entry) => {
            // When only a small portion of header is visible, show to-top
            const shouldShowToTop = entry.intersectionRatio < 0.25;

            if (shouldShowToTop && !isToTopVisible) {
               showToTop();
            } else if (!shouldShowToTop && isToTopVisible) {
               hideToTop();
            }
         });
      },
      {
         threshold: [0, 0.25, 0.5, 0.75, 1], // Multiple thresholds for fine control
         rootMargin: '0px',
      }
   );

   // Start observing the header
   toTopObserver.observe(header);

   function showToTop() {
      if (isToTopVisible) return;

      gsap.to(toTopLink, {
         opacity: 1,
         pointerEvents: 'auto',
         duration: 0.3,
         ease: 'power2.out',
      });

      isToTopVisible = true;
   }

   function hideToTop() {
      if (!isToTopVisible) return;

      gsap.to(toTopLink, {
         opacity: 0,
         pointerEvents: 'none',
         duration: 0.3,
         ease: 'power2.in',
      });

      isToTopVisible = false;
   }
}

/* MARK: Main Initialization
----------------------------------------------- */
function init() {
   // Initialize Alpine.js
   Alpine.start();

   // Initialize Swipers first
   initSwipers();

   // Initialize instruction hover effects
   initInstructionHovers();

   // Initialize modal system
   initModals();

   // Initialize parallax effects
   initParallaxEffects();

   // Initialize counter animations
   window.annualReportCounter = new AnnualReportCounter();

   // Initialize Intersection Observer features
   initScrollNavigation();
   initToTopLink();

   // Initialize entrance animations after Swiper is ready
   setTimeout(() => {
      initEntranceAnimations();
   }, 100);
}

// Wait for DOM and initialize everything
document.addEventListener('DOMContentLoaded', init);

/* MARK: Anchor links
----------------------------------------------- */
document.addEventListener('click', function (e) {
   const toTopLink = e.target.closest('a.to-top');
   if (toTopLink) {
      e.preventDefault();
      e.stopPropagation();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
   }
});

// General anchor link handler
document.addEventListener('click', function (e) {
   // Find the closest anchor element (in case click is on child element like img)
   const anchor = e.target.closest('a[href^="#"]');

   if (anchor && !anchor.classList.contains('to-top')) {
      e.preventDefault();

      const href = anchor.getAttribute('href');
      const target = document.querySelector(href);
      if (target) {
         target.scrollIntoView({ behavior: 'smooth' });
      }
   }
});
